import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useRouteMatch } from 'react-router-dom'

import { getEmployeeBenefitParticipationStatusHistory } from 'core'
import getEmployeeBenefitHistory from 'core/application/companies/getEmployeeBenefitHistory'
import { EmployeeDetailsResponse, getEmployeeDetails } from 'core/application/companies/getEmployeeDetails'
import getEmployeeFamilyHistory from 'core/application/companies/getEmployeeFamilyHistory'

import EmployeeProfileDetails from 'common/components/employeeProfile/EmployeeProfileDetails'
import {
  benefitParticipationStatusHistoryColumns,
  employeeHistoryColumns,
  familyHistoryColumns,
} from 'common/components/employeeProfile/employeeHistoryStatus/employeeHistoryColumns'

import { Card, Col, Row, Skeleton, Table } from 'common/components'
import { useLoginUserV2, useQueryRequestV2 } from 'common/hooks'

const ViewProfile = () => {
  const { t: translate } = useTranslation()
  const { id } = useParams<any>()
  const match = useRouteMatch()
  const { user } = useLoginUserV2()
  const employeeId = id ?? user.companyUserId

  const { data: employeeDetails } = useQueryRequestV2<EmployeeDetailsResponse>({
    initialState: null,
    queryFn: () => getEmployeeDetails(employeeId),
  })

  const {
    data: employeeHistoryStatusData,
    isLoading: isEmployeeHistoryStatusDataLoading,
    sendQuery: sendGetEmployeeHistoryStatusData,
  } = useQueryRequestV2({ initialState: null })

  const [employeeHistoryStatusDataFilter, setEmployeeHistoryStatusDataFilter] = useState<any>({
    pageNumber: 1,
    pageSize: 10,
    orderColumn: 'lastModifiedAt',
    orderDescending: false,
    searchValue: null,
  })
  const [familyHistoryStatusDataFilter, setFamilyHistoryStatusDataFilter] = useState<any>({
    pageNumber: 1,
    pageSize: 10,
    orderColumn: 'lastModifiedAt',
    orderDescending: false,
    searchValue: null,
  })

  const {
    data: familyHistoryStatusData,
    isLoading: isFamilyHistoryStatusDataLoading,
    sendQuery: sendGetFamilyHistoryStatusData,
  } = useQueryRequestV2({ initialState: null })

  const [benefitParticipationStatusHistoryFilter, setBenefitParticipationStatusHistoryFilter] = useState<any>({
    pageNumber: 1,
    pageSize: 10,
    orderColumn: 'modifyDate',
    orderDescending: true,
    searchValue: null,
  })
  const {
    data: benefitParticipationStatusHistoryData,
    isLoading: isBenefitParticipationStatusHistoryDataLoading,
    sendQuery: sendGetBenefitParticipationStatusHistoryData,
  } = useQueryRequestV2({ initialState: null })

  useEffect(() => {
    ;(async () => {
      if (employeeDetails) {
        await sendGetEmployeeHistoryStatusData(() =>
          getEmployeeBenefitHistory(employeeHistoryStatusDataFilter, employeeDetails.id),
        )
      }
    })()
  }, [employeeDetails, employeeHistoryStatusDataFilter])

  useEffect(() => {
    ;(async () => {
      if (employeeDetails) {
        await sendGetFamilyHistoryStatusData(() =>
          getEmployeeFamilyHistory(familyHistoryStatusDataFilter, employeeDetails.id),
        )
      }
    })()
  }, [employeeDetails, familyHistoryStatusDataFilter])

  useEffect(() => {
    ;(async () => {
      if (employeeDetails) {
        await sendGetBenefitParticipationStatusHistoryData(() =>
          getEmployeeBenefitParticipationStatusHistory(benefitParticipationStatusHistoryFilter, employeeDetails.id),
        )
      }
    })()
  }, [employeeDetails, benefitParticipationStatusHistoryFilter])

  const onEmployeeHistoryTableChange = (pagination: any) => {
    setEmployeeHistoryStatusDataFilter({
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    })
  }

  const onFamilyHistoryTableChange = (pagination: any) => {
    setFamilyHistoryStatusDataFilter({
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    })
  }

  const onBenefitParticipationStatusHistoryTableChange = (pagination: any) => {
    setBenefitParticipationStatusHistoryFilter({
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    })
  }

  return (
    <Skeleton active={true} loading={!employeeDetails}>
      {employeeDetails && (
        <Row>
          <Col span={24}>
            <EmployeeProfileDetails
              employeeDetails={employeeDetails}
              editProfileUrl={`${match.path}/edit`}
              profileTitle={translate('employee-profile.my-profile-title')}
            />

            <Row className="mt-24">
              <Col span={24}>
                <Card title={translate('admin-dashboard.manage-people.view-employee.benefit-history.title')}>
                  <Row>
                    <Col span={24}>
                      <Table
                        dataSource={employeeHistoryStatusData?.items}
                        onChange={onEmployeeHistoryTableChange}
                        loading={isEmployeeHistoryStatusDataLoading}
                        columns={employeeHistoryColumns(translate, employeeHistoryStatusDataFilter)}
                        pagination={{
                          total: employeeHistoryStatusData?.totalItemCount,
                          pageSize: employeeHistoryStatusData?.pageSize,
                          current: employeeHistoryStatusData?.pageNumber,
                          showSizeChanger: true,
                          pageSizeOptions: ['10', '20', '30', '40'],
                        }}
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>

            {employeeDetails.shouldDisplayFamilyStatus && (
              <Row className="mt-24">
                <Col span={24}>
                  <Card title={translate('admin-dashboard.manage-people.view-employee.family-history.title')}>
                    <Row>
                      <Col span={24}>
                        <Table
                          dataSource={familyHistoryStatusData?.items}
                          onChange={onFamilyHistoryTableChange}
                          loading={isFamilyHistoryStatusDataLoading}
                          columns={familyHistoryColumns(translate, familyHistoryStatusDataFilter)}
                          pagination={{
                            total: familyHistoryStatusData?.totalItemCount,
                            pageSize: familyHistoryStatusData?.pageSize,
                            current: familyHistoryStatusData?.pageNumber,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '30', '40'],
                          }}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            )}

            {employeeDetails.shouldDisplayBenefitParticipationStatusHistory && (
              <Row className="mt-24">
                <Col span={24}>
                  <Card
                    title={translate(
                      'admin-dashboard.manage-people.view-employee.benefit-participation-status-history.title',
                    )}
                  >
                    <Row>
                      <Col span={24}>
                        <Table
                          className={'t-employee-benefit-participation-status-history-table'}
                          dataSource={benefitParticipationStatusHistoryData?.items}
                          onChange={onBenefitParticipationStatusHistoryTableChange}
                          loading={isBenefitParticipationStatusHistoryDataLoading}
                          columns={benefitParticipationStatusHistoryColumns(
                            translate,
                            benefitParticipationStatusHistoryFilter,
                          )}
                          pagination={{
                            total: benefitParticipationStatusHistoryData?.totalItemCount,
                            pageSize: benefitParticipationStatusHistoryData?.pageSize,
                            current: benefitParticipationStatusHistoryData?.pageNumber,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '30', '40'],
                          }}
                          scroll={{ x: 1000 }}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      )}
    </Skeleton>
  )
}

export default ViewProfile
